// GFM element styling shared by every `react-markdown` pipeline in this feature — currently the
// legacy/old-backend pipeline (`CitationsMarkdownComponents.buildMarkdownComponents`, driving
// `MessageContent.jsx`) and the agentic/new-backend pipeline (`AgenticMarkdownComponents`, driving
// `../../../agenticRun/components/AgenticFinalAnswer.jsx`). Both pipelines need the exact same MUI
// treatment for headings/paragraphs/tables/etc — the only thing that differs between them is WHICH
// inline `[[...]]` marker grammar each recognizes inside a text node (legacy: `[[citations:JSON]]`;
// agentic: `[[type:len:payload]]` + `[[cite:id,...]]`) — so that recognition is injected here as a
// single `wrapChildren(children, keyPrefix)` callback rather than duplicated per pipeline.
import {Box} from '@mui/material';

/**
 * @param {(children: unknown, keyPrefix: string) => unknown} wrapChildren applies a pipeline's own
 *   inline-marker substitution to a rendered element's children before they're re-rendered.
 * @returns {Record<string, Function>} a `react-markdown` `components` map.
 */
export function buildStyledMarkdownComponents(wrapChildren) {
    return {
        h1: ({node, children, ...props}) => <h1 {...props}>{wrapChildren(children, 'h1')}</h1>,
        h2: ({node, children, ...props}) => <h2 {...props}>{wrapChildren(children, 'h2')}</h2>,
        h3: ({node, children, ...props}) => <h3 {...props}>{wrapChildren(children, 'h3')}</h3>,
        h4: ({node, children, ...props}) => <h4 {...props}>{wrapChildren(children, 'h4')}</h4>,
        h5: ({node, children, ...props}) => <h5 {...props}>{wrapChildren(children, 'h5')}</h5>,
        h6: ({node, children, ...props}) => <h6 {...props}>{wrapChildren(children, 'h6')}</h6>,
        p: ({node, children, ...props}) => <p {...props}>{wrapChildren(children, 'p')}</p>,
        ul: ({node, children, ...props}) => (
            <Box
                component="ul"
                sx={{pl: 2.5, my: 1, listStyleType: 'disc', listStylePosition: 'outside'}}
                {...props}
            >
                {wrapChildren(children, 'ul')}
            </Box>
        ),
        ol: ({node, children, ...props}) => (
            <Box
                component="ol"
                sx={{pl: 2.5, my: 1, listStyleType: 'decimal', listStylePosition: 'outside'}}
                {...props}
            >
                {wrapChildren(children, 'ol')}
            </Box>
        ),
        li: ({node, children, ...props}) => (
            <Box component="li" sx={{display: 'list-item', mb: 0.5}} {...props}>
                {wrapChildren(children, 'li')}
            </Box>
        ),
        blockquote: ({node, children, ...props}) =>
            <blockquote {...props}>{wrapChildren(children, 'blockquote')}</blockquote>,
        a: ({node, children, ...props}) => <a {...props}>{wrapChildren(children, 'a')}</a>,
        strong: ({node, children, ...props}) => <strong {...props}>{wrapChildren(children, 'strong')}</strong>,
        em: ({node, children, ...props}) => <em {...props}>{wrapChildren(children, 'em')}</em>,
        table: ({node, children, ...props}) => (
            <Box sx={{overflowX: 'auto', my: 1.5}}>
                <Box
                    component="table"
                    sx={{
                        width: '100%',
                        minWidth: 520,
                        borderCollapse: 'collapse',
                        borderSpacing: 0,
                        '& th, & td': {
                            border: '1px solid',
                            borderColor: 'divider',
                            px: 1,
                            py: 0.75,
                            textAlign: 'left',
                            verticalAlign: 'top',
                        },
                        '& th': {
                            bgcolor: 'action.hover',
                            fontWeight: 600,
                        },
                    }}
                    {...props}
                >
                    {wrapChildren(children, 'table')}
                </Box>
            </Box>
        ),
        thead: ({node, children, ...props}) => <thead {...props}>{wrapChildren(children, 'thead')}</thead>,
        tbody: ({node, children, ...props}) => <tbody {...props}>{wrapChildren(children, 'tbody')}</tbody>,
        tr: ({node, children, ...props}) => <tr {...props}>{wrapChildren(children, 'tr')}</tr>,
        td: ({node, children, ...props}) => <td {...props}>{wrapChildren(children, 'td')}</td>,
        th: ({node, children, ...props}) => <th {...props}>{wrapChildren(children, 'th')}</th>,
    };
}

export default buildStyledMarkdownComponents;
